<template>
  <div class="llm-voice-test">
    <audio ref="remoteAudioEl" class="sim-remote-audio" autoplay playsinline />
    <div class="sim-card">
      <!-- 待机：图1 / 图2 -->
      <div v-show="!hasSessionUi" class="sim-idle">
        <h2 class="sim-idle__title">开始语音测试</h2>
        <p class="sim-idle__desc">
          无论选用哪种模型，均以语音通话测试；双方说话会逐字识别成文字展示
        </p>
        <button
          type="button"
          class="sim-phone"
          :class="{ 'is-busy': isConnecting }"
          :disabled="isConnecting"
          @click="startTest"
        >
          <span class="sim-phone__ring" aria-hidden="true" />
          <span class="sim-phone__btn">
            <svg viewBox="0 0 24 24" width="42" height="42" fill="none" aria-hidden="true">
              <path
                d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"
                stroke="currentColor"
                stroke-width="1.75"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
        </button>
        <div class="sim-idle__hint">{{ idleHint }}</div>
      </div>

      <!-- 通话中 / 已结束仍保留记录：图3 -->
      <div v-show="hasSessionUi" class="sim-call">
        <div class="sim-call__timer">通话时长 {{ formattedElapsed }}</div>
        <div v-if="isLive && !messages.length" class="sim-call__waiting">
          已接通，等待机器人开场白；请先听对方说完再回答
        </div>
        <div ref="chatList" class="sim-call__msgs">
          <div
            v-for="(msg, index) in messages"
            :key="msg.id"
            class="sim-msg"
            :class="'sim-msg--' + (msg.role === 'log' ? 'system' : msg.role)"
          >
            <div v-if="msg.role === 'hangup'" class="sim-msg__hangup">
              <span class="sim-msg__hangup-line" />
              <span class="sim-msg__hangup-text">{{ msg.content }}</span>
              <span class="sim-msg__hangup-line" />
            </div>
            <div v-else-if="msg.role === 'system' || msg.role === 'log'" class="sim-msg__sys">{{ msg.content }}</div>
            <template v-else>
              <div v-if="msg.role !== 'user'" class="sim-msg__avatar sim-msg__avatar--bot" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7">
                  <rect x="5" y="8" width="14" height="10" rx="3" />
                  <circle cx="9" cy="13" r="1.2" fill="currentColor" stroke="none" />
                  <circle cx="15" cy="13" r="1.2" fill="currentColor" stroke="none" />
                  <path d="M12 8V5" />
                  <circle cx="12" cy="4" r="1" fill="currentColor" stroke="none" />
                </svg>
              </div>
              <div class="sim-msg__bubble">
                {{ displayContent(msg) }}<span v-if="msg.isTemporary && index === messages.length - 1" class="sim-msg__cursor" />
              </div>
              <div v-if="msg.role === 'user'" class="sim-msg__avatar sim-msg__avatar--user" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="16" height="16">
                  <path
                    fill="currentColor"
                    d="M12 12a4 4 0 100-8 4 4 0 000 8zm0 2c-4.42 0-8 1.79-8 4v2h16v-2c0-2.21-3.58-4-8-4z"
                  />
                </svg>
              </div>
            </template>
          </div>
        </div>
        <div class="sim-call__footer" :class="{ 'is-ended': callEnded }">
          <template v-if="callEnded">
            <div class="sim-call__ended-hint">通话已结束，可重置后重新测试</div>
            <div class="sim-call__ended-actions">
              <button type="button" class="sim-btn sim-btn--reset" @click="resetTest">重置</button>
              <button type="button" class="sim-btn sim-btn--close" @click="closeTest">关闭测试</button>
            </div>
          </template>
          <template v-else>
            <button
              type="button"
              class="sim-ptt"
              :class="{ 'is-talking': talking }"
              :disabled="!canTalk"
              @click="toggleTalk"
            >
              <span class="sim-ptt__wave" aria-hidden="true">
                <span v-for="n in 12" :key="'bar-' + n" class="sim-ptt__bar" />
              </span>
              <span class="sim-ptt__label">{{ pttLabel }}</span>
            </button>
            <button type="button" class="sim-btn sim-btn--reset" @click="resetTest">重置</button>
            <button
              v-if="isLive"
              type="button"
              class="sim-btn sim-btn--stop"
              @click="stopTest"
            >
              终止测试
            </button>
            <button v-else type="button" class="sim-btn sim-btn--start" @click="startTest">
              开始测试
            </button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { createGatewaySession } from './gatewaySession'
import { pickOpeningText } from './debugFlow'

export default {
  name: 'LlmVoiceTest',
  props: {
    voiceCall: {
      type: Object,
      default: null,
    },
    openingText: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      sessionStatus: 'idle',
      voiceStatus: 'idle',
      traces: [],
      talking: false,
      callStartedAt: null,
      nowTick: 0,
      tickTimer: null,
      hangupHandled: false,
      userStopped: false,
      hadCall: false,
      openingShown: '',
      liveTurns: [],
    }
  },
  computed: {
    ...mapState('scene', {
      storeScene: 'scene',
      storeSceneId: 'sceneId',
      storeSceneName: 'sceneName',
    }),
    resolvedOpening() {
      return pickOpeningText(this.storeScene, this.openingText, this.voiceCall)
    },
    openingReady() {
      const full = this.resolvedOpening
      if (!full) return true
      return this.openingShown.length >= full.length
    },
    messages() {
      return this.liveTurns
    },
    isConnecting() {
      return this.sessionStatus === 'connecting' || this.voiceStatus === 'connecting'
    },
    isLive() {
      return this.sessionStatus === 'connecting' || this.sessionStatus === 'connected'
    },
    hasSessionUi() {
      return this.isLive || this.messages.length > 0
    },
    callEnded() {
      return !this.isLive && this.messages.length > 0
    },
    canTalk() {
      return this.isLive && this.voiceStatus === 'calling'
    },
    pttLabel() {
      if (!this.canTalk) return '接通后即可说话'
      return this.talking ? '正在收音，点击暂停' : '点击模拟客户说话'
    },
    idleHint() {
      if (this.isConnecting) return '连接中...'
      return '点击开始'
    },
    elapsedSeconds() {
      if (!this.callStartedAt) return 0
      const end = this.nowTick || this.callStartedAt
      return Math.max(0, Math.floor((end - this.callStartedAt) / 1000))
    },
    formattedElapsed() {
      const s = this.elapsedSeconds
      const mm = String(Math.floor(s / 60)).padStart(2, '0')
      const ss = String(s % 60).padStart(2, '0')
      return `${mm}:${ss}`
    },
  },
  watch: {
    messages() {
      this.$nextTick(() => this.scrollChat())
    },
    sessionStatus(val) {
      if (val === 'connecting') {
        this.hangupHandled = false
        this.userStopped = false
      }
    },
    voiceStatus(val) {
      if (val === 'calling') {
        this.hadCall = true
        this.talking = true
        this.gateway.setMicMuted(false)
        if (!this.callStartedAt) {
          this.callStartedAt = Date.now()
          this.nowTick = Date.now()
          this.startTick()
        }
      }
      if (val === 'error' || val === 'idle') {
        this.freezeCallTimer()
      }
      if (val === 'error') {
        this.gateway.disconnect()
        this.sessionStatus = 'disconnected'
      }
    },
  },
  created() {
    this.gateway = createGatewaySession()
    this.unsubs = [
      this.gateway.on('status', (s) => {
        this.sessionStatus = s
      }),
      this.gateway.on('voiceStatus', (s) => {
        this.voiceStatus = s
      }),
      this.gateway.on('transcript', (payload) => {
        this.applyGatewayTranscript(payload)
      }),
      this.gateway.on('error', (msg) => {
        this.$Message && this.$Message.error && this.$Message.error(msg)
      }),
      this.gateway.on('hangup', () => {
        this.onRemoteHangup()
      }),
    ]
  },
  beforeDestroy() {
    this.teardown()
    ;(this.unsubs || []).forEach((fn) => fn && fn())
  },
  methods: {
    displayContent(msg) {
      const raw = msg && msg.content != null ? String(msg.content) : ''
      if (!raw && msg && msg.isTemporary) return '识别中'
      if (msg.role === 'user' && raw.endsWith('---')) {
        return raw.slice(0, -3).replace(/\s+$/, '')
      }
      return raw
    },
    touchTurns() {
      this.liveTurns = this.liveTurns.slice()
    },
    lastTurn() {
      return this.liveTurns.length ? this.liveTurns[this.liveTurns.length - 1] : null
    },
    lastUserTurn() {
      for (let i = this.liveTurns.length - 1; i >= 0; i -= 1) {
        if (this.liveTurns[i].role === 'user') return this.liveTurns[i]
      }
      return null
    },
    sealOpenUserTurns() {
      let changed = false
      this.liveTurns.forEach((turn) => {
        if (turn && turn.role === 'user' && turn.isTemporary) {
          turn.isTemporary = false
          changed = true
        }
      })
      if (changed) this.touchTurns()
    },
    ingestChatTraces(list) {
      const src = Array.isArray(list) ? list : []
      const start = this._traceIngestAt || 0
      if (start >= src.length) return
      this._traceIngestAt = src.length
      let msgs = []
      try {
        msgs = tracesToMessages(src.slice(start))
      } catch (e) {
        console.warn('[llm-debug] parse chat traces failed', e)
        return
      }
      msgs.forEach((msg) => {
        if (!msg) return
        if (msg.role === 'system') {
          this.liveTurns.push({
            id: msg.id,
            role: 'system',
            content: msg.content,
          })
          return
        }
        if (msg.role === 'bot') this.applyBotFromTrace(msg.content, msg.isTemporary)
      })
    },
    applyBotFromTrace(text, isTemporary) {
      const content = String(text || '').trim()
      if (!content) return
      this.sealOpenUserTurns()
      if (this.resolvedOpening && sameOpening(content, this.resolvedOpening)) {
        this.clearOpeningTypewriter()
        this.openingShown = this.resolvedOpening
        this.upsertOpening(this.resolvedOpening, this.resolvedOpening)
        if (this.talking && this.canTalk) this.startLocalAsr()
        return
      }
      const last = this.lastTurn()
      if (last && last.role === 'bot' && last.id !== 'seed-opening') {
        const shown = String(last.content || '')
        const target = this._botTypeTurn === last ? this._botTypeFull : shown
        if (content === shown || content === target) {
          if (!isTemporary) this.finishBotTypewriter()
          return
        }
        if (content.indexOf(target) === 0 || content.indexOf(shown) === 0) {
          this.queueBotTypewriter(last, content)
          return
        }
        if (target.indexOf(content) === 0 || shown.indexOf(content) === 0) return
      }
      this.finishBotTypewriter()
      const turn = {
        id: `bot-${Date.now()}-${this.liveTurns.length}`,
        role: 'bot',
        content: '',
        isTemporary: true,
      }
      this.liveTurns.push(turn)
      this.queueBotTypewriter(turn, content)
    },
    queueBotTypewriter(turn, full) {
      const target = String(full || '')
      if (!turn) return
      if (this._botTypeTurn === turn) {
        this._botTypeFull = target
        turn.isTemporary = true
        return
      }
      this.finishBotTypewriter()
      this._botTypeTurn = turn
      this._botTypeFull = target
      turn.isTemporary = true
      if (!turn.content) turn.content = ''
      this.touchTurns()
      this._botTypeTimer = setInterval(() => {
        const dest = this._botTypeTurn
        const fullText = this._botTypeFull || ''
        if (!dest) {
          this.clearBotTypewriter()
          return
        }
        const cur = String(dest.content || '')
        if (cur.length >= fullText.length) {
          dest.content = fullText
          dest.isTemporary = false
          this.clearBotTypewriter()
          this.touchTurns()
          return
        }
        dest.content = fullText.slice(0, cur.length + 1)
        this.touchTurns()
      }, 22)
    },
    finishBotTypewriter() {
      if (this._botTypeTurn) {
        this._botTypeTurn.content = this._botTypeFull || this._botTypeTurn.content
        this._botTypeTurn.isTemporary = false
      }
      this.clearBotTypewriter()
      this.touchTurns()
    },
    clearBotTypewriter() {
      if (this._botTypeTimer) {
        clearInterval(this._botTypeTimer)
        this._botTypeTimer = null
      }
      this._botTypeTurn = null
      this._botTypeFull = ''
    },
    upsertOpening(shown, full) {
      const turn = {
        id: 'seed-opening',
        role: 'bot',
        content: shown,
        isTemporary: shown.length < full.length,
      }
      if (this.liveTurns[0] && this.liveTurns[0].id === 'seed-opening') {
        this.$set(this.liveTurns, 0, turn)
      } else {
        this.liveTurns = [turn, ...this.liveTurns]
      }
    },
    stripCommittedUserPrefix(text) {
      let rest = String(text || '').trim()
      if (!rest) return ''
      const sealed = this.liveTurns
        .filter((turn) => turn.role === 'user' && !turn.isTemporary)
        .map((turn) => String(turn.content || '').trim())
        .filter(Boolean)
      const joined = sealed.join('')
      if (joined && rest.indexOf(joined) === 0) {
        return rest.slice(joined.length).trim()
      }
      sealed.forEach((prev) => {
        if (prev && rest.indexOf(prev) === 0) {
          rest = rest.slice(prev.length).trim()
        }
      })
      return rest
    },
    setUserPartial(text) {
      this.applyUserSpeech(text, false)
    },
    commitUserTurn(text) {
      this.applyUserSpeech(text, true)
    },
    applyUserSpeech(text, isFinal) {
      const content = this.stripCommittedUserPrefix(text)
      if (!content) return
      const last = this.lastTurn()
      if (last && last.role === 'user') {
        const prev = String(last.content || '').trim()
        if (last.isTemporary) {
          last.content = content
          last.isTemporary = !isFinal
          this.touchTurns()
          return
        }
        if (prev === content) return
      }
      this.liveTurns.push({
        id: `u-${Date.now()}-${this.liveTurns.length}`,
        role: 'user',
        content,
        isTemporary: !isFinal,
      })
    },
    startOpeningTypewriter() {
      const full = this.resolvedOpening
      this.clearOpeningTypewriter()
      if (!full) {
        if (this.talking && this.canTalk) this.startLocalAsr()
        return
      }
      this.openingShown = full
      this.upsertOpening(full, full)
      if (this.talking && this.canTalk) this.startLocalAsr()
    },
    clearOpeningTypewriter() {
      if (this.openingTimer) {
        clearInterval(this.openingTimer)
        this.openingTimer = null
      }
    },
    startLocalAsr() {
      this._keepAsr = true
      this.startAsrEngine()
    },
    startAsrEngine() {
      if (this._asr) return
      const SR = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition)
      if (!SR) return
      const rec = new SR()
      rec.lang = 'zh-CN'
      rec.continuous = true
      rec.interimResults = true
      rec.maxAlternatives = 1
      rec.onresult = (event) => {
        let interim = ''
        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          const piece = event.results[i][0] && event.results[i][0].transcript
          if (!piece) continue
          if (event.results[i].isFinal) this.commitUserTurn(piece)
          else interim += piece
        }
        if (interim) this.setUserPartial(interim)
      }
      rec.onerror = () => {}
      rec.onend = () => {
        const last = this.lastTurn()
        if (last && last.role === 'user' && last.isTemporary) {
          last.isTemporary = false
          this.touchTurns()
        }
        if (this._asr === rec) this._asr = null
        if (this._keepAsr && this.talking) this.startAsrEngine()
      }
      try {
        rec.start()
        this._asr = rec
      } catch (e) {
        this._asr = null
      }
    },
    stopLocalAsr() {
      this._keepAsr = false
      this._lastUserCommitAt = 0
      const rec = this._asr
      this._asr = null
      if (!rec) return
      rec.onresult = null
      rec.onerror = null
      rec.onend = null
      try {
        rec.stop()
      } catch (e) {
        try {
          rec.abort()
        } catch (err) {
          /* noop */
        }
      }
    },
    scrollChat() {
      const el = this.$refs.chatList
      if (!el) return
      el.scrollTop = el.scrollHeight
    },
    startTick() {
      this.clearTick()
      this.nowTick = Date.now()
      this.tickTimer = setInterval(() => {
        this.nowTick = Date.now()
      }, 250)
    },
    freezeCallTimer() {
      if (this.callStartedAt) this.nowTick = Date.now()
      this.clearTick()
    },
    clearTick() {
      if (this.tickTimer) {
        clearInterval(this.tickTimer)
        this.tickTimer = null
      }
    },
    bindRemoteStream(stream) {
      const el = this.$refs.remoteAudioEl
      if (el && stream) {
        try {
          el.srcObject = stream
          el.muted = false
          el.volume = 1
          const p = el.play()
          if (p && typeof p.catch === 'function') p.catch(() => {})
        } catch (e) {
          /* noop */
        }
      }
      this.sip.playRemote()
    },
    applyGatewayTranscript(payload) {
      if (this.callEnded) return
      if (!payload || !payload.key) return
      const role = payload.role === 'user' ? 'user' : 'bot'
      let turn = this.liveTurns.find((item) => item.id === payload.key)
      if (!turn) {
        if (role === 'bot') this.sealOpenUserTurns()
        turn = {
          id: payload.key,
          role,
          content: '',
          isTemporary: true,
        }
        this.liveTurns.push(turn)
      }
      if (!turn.isTemporary && !payload.final) return
      if (payload.replace || payload.final) {
        turn.content = String(payload.text || turn.content || '')
      } else {
        turn.content = String(turn.content || '') + String(payload.text || '')
      }
      turn.isTemporary = !payload.final
      this.touchTurns()
    },
    startTest() {
      const flow = this.storeScene
      if (!flow || typeof flow !== 'object' || !Object.keys(flow).length) {
        this.$Message.error('话术数据未加载，无法开始调试')
        return
      }
      if (!this.storeSceneId) {
        this.$Message.error('缺少话术 ID，无法开始调试')
        return
      }
      this.gateway.unlockAudio()
      this.talking = false
      this.userStopped = false
      this.hangupHandled = false
      this.hadCall = false
      this.openingShown = ''
      this.liveTurns = []
      this.callStartedAt = Date.now()
      this.nowTick = Date.now()
      this.startTick()
      this.sessionStatus = 'connecting'
      this.voiceStatus = 'connecting'
      return this.gateway.connect({ scriptId: this.storeSceneId })
    },
    appendHangupTurns(kind) {
      this.sealOpenUserTurns()
      if (this.liveTurns.some((turn) => turn.role === 'hangup')) return
      const label = kind === 'callee' ? '[被叫挂机]' : '[主叫挂机]'
      this.liveTurns.push({
        id: 'hangup-party',
        role: 'hangup',
        content: label,
      })
      this.liveTurns.push({
        id: 'hangup-ended',
        role: 'system',
        content: '通话已结束',
      })
      this.touchTurns()
      this.hangupHandled = true
    },
    onRemoteHangup() {
      this.freezeCallTimer()
      if (this.userStopped || this.hangupHandled) return
      if (!this.hadCall && !this.liveTurns.length) return
      this.appendHangupTurns('caller')
    },
    stopTest() {
      if (!this.isLive && this.callEnded) return
      this.userStopped = true
      this.stopTalking()
      this.freezeCallTimer()
      this.appendHangupTurns('callee')
      this.gateway.hangup()
      this.sessionStatus = 'idle'
      this.voiceStatus = 'idle'
    },
    resetTest() {
      if (this._resetting) return
      this._resetting = true
      this.userStopped = true
      this.hangupHandled = true
      this.stopTalking()
      this.liveTurns = []
      this.hadCall = false
      this.sessionStatus = 'connecting'
      this.voiceStatus = 'connecting'
      this.gateway
        .hangup({ silent: true })
        .then(() => this.startTest())
        .finally(() => {
          this._resetting = false
        })
    },
    closeTest() {
      this.userStopped = true
      this.stopTalking()
      this.gateway && this.gateway.hangup()
      this.clearTick()
      this.callStartedAt = null
      this.nowTick = 0
      this.liveTurns = []
      this.openingShown = ''
      this.hangupHandled = false
      this.hadCall = false
      this.sessionStatus = 'idle'
      this.voiceStatus = 'idle'
    },
    toggleTalk() {
      if (!this.canTalk) return
      this.gateway.unlockAudio()
      if (this.talking) this.stopTalking()
      else this.startTalking()
    },
    startTalking() {
      this.talking = true
      this.gateway.setMicMuted(false)
    },
    stopTalking() {
      this.talking = false
      this.gateway.setMicMuted(true)
      const last = this.lastTurn()
      if (last && last.role === 'user' && last.isTemporary) {
        last.isTemporary = false
        this.touchTurns()
      }
    },
    teardown() {
      this.stopTalking()
      this.gateway && this.gateway.hangup()
      this.clearTick()
    },
  },

}
</script>

<style lang="scss" scoped>
.llm-voice-test {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  align-self: stretch;
  min-height: 0;
  height: 100%;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  border: 1px solid #e8e8e8;
}

.sim-remote-audio {
  position: absolute;
  left: 0;
  top: 0;
  width: 1px;
  height: 1px;
  opacity: 0.01;
}

.sim-card {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, #eef2ff 0%, #f7f4ff 55%, #fff 100%);
}

.sim-idle {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 24px 48px;
  text-align: center;
}

.sim-idle__title {
  margin: 0 0 12px;
  font-size: 26px;
  font-weight: 700;
  color: #1b2a4a;
  letter-spacing: 0.02em;
}

.sim-idle__desc {
  margin: 0 0 36px;
  max-width: 280px;
  font-size: 14px;
  line-height: 1.7;
  color: #9aa3b5;
}

.sim-phone {
  position: relative;
  width: 88px;
  height: 88px;
  border: none;
  padding: 0;
  background: transparent;
  cursor: pointer;

  &:disabled {
    cursor: wait;
  }

  &:hover:not(:disabled) .sim-phone__btn,
  &:focus-visible:not(:disabled) .sim-phone__btn {
    animation: none;
    transform: scale(1.05);
    box-shadow: 0 6px 28px rgba(79, 110, 247, 0.5);
  }
}

.sim-phone__ring {
  display: none;
}

.sim-phone__btn {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 88px;
  height: 88px;
  border-radius: 50%;
  color: #fff;
  background: #3d63ff;
  border: 1.5px solid rgba(255, 255, 255, 0.72);
  box-shadow: 0 4px 20px rgba(79, 110, 247, 0.35);
  animation: simBreathe 2.4s ease-in-out infinite;
  box-sizing: border-box;
}

.sim-phone.is-busy .sim-phone__btn {
  animation-duration: 1.2s;
}

@keyframes simBreathe {
  0%,
  100% {
    transform: scale(1);
    box-shadow: 0 4px 16px rgba(79, 110, 247, 0.3);
  }
  50% {
    transform: scale(1.06);
    box-shadow: 0 6px 24px rgba(79, 110, 247, 0.45);
  }
}

.sim-idle__hint {
  margin-top: 16px;
  font-size: 14px;
  color: #b0b6c3;
}

.sim-call {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 12px 12px 0;
}

.sim-call__timer {
  align-self: flex-start;
  margin-bottom: 10px;
  padding: 4px 12px;
  border-radius: 999px;
  background: #eceff5;
  color: #5c6578;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}

.sim-call__waiting {
  margin: 12px 4px 0;
  font-size: 12px;
  color: #9aa3b5;
  text-align: center;
}

.sim-call__msgs {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 2px 8px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.sim-msg {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.sim-msg--user {
  justify-content: flex-end;
}

.sim-msg--system,
.sim-msg--hangup {
  justify-content: center;
}

.sim-msg__sys {
  font-size: 12px;
  color: #b0b6c3;
}

.sim-msg__hangup {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 4px 0;
}

.sim-msg__hangup-line {
  flex: 1;
  height: 1px;
  background: #e6e8ee;
}

.sim-msg__hangup-text {
  flex-shrink: 0;
  font-size: 12px;
  color: #b0b6c3;
}

.sim-call__ended-hint {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: #8b93a7;
}

.sim-call__ended-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.sim-msg__avatar {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sim-msg__avatar--bot {
  color: #4f6ef7;
  background: #ecefff;
}

.sim-msg__avatar--user {
  color: #fff;
  background: #3d63ff;
}

.sim-msg__bubble {
  max-width: 78%;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.65;
  word-break: break-word;
  text-align: left;
}

.sim-msg--bot .sim-msg__bubble,
.sim-msg--agent .sim-msg__bubble {
  background: #fff;
  color: #2c3340;
  border: 1px solid #eceef3;
  border-radius: 12px 12px 12px 4px;
}

.sim-msg--user .sim-msg__bubble {
  background: #3d63ff;
  color: #fff;
  border-radius: 12px 12px 4px 12px;
}

.sim-msg__cursor {
  display: inline-block;
  width: 1px;
  height: 1em;
  margin-left: 2px;
  vertical-align: -2px;
  background: currentColor;
  animation: sim-cursor 0.9s steps(1) infinite;
}

@keyframes sim-cursor {
  50% {
    opacity: 0;
  }
}

.sim-call__footer {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 4px -12px 0;
  padding: 12px 12px 12px;
  border-top: 1px solid #e8eaef;
  background: #f5f6fa;

  &.is-ended {
    justify-content: space-between;
  }
}

.sim-ptt {
  flex: 1 1 auto;
  min-width: 0;
  height: 52px;
  border: 1px solid #eceef3;
  border-radius: 999px;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
  touch-action: none;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }

  &.is-talking {
    border-color: #c9d4ff;
    background: #f4f6ff;
  }
}

.sim-ptt__wave {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  height: 22px;
}

.sim-ptt__bar {
  display: block;
  width: 3px;
  height: 8px;
  border-radius: 999px;
  animation: sim-ptt-sweep 1s ease-in-out infinite;
}

.sim-ptt__bar:nth-child(1) { background: #f06292; animation-delay: 0s; }
.sim-ptt__bar:nth-child(2) { background: #ec407a; animation-delay: 0.05s; }
.sim-ptt__bar:nth-child(3) { background: #e040fb; animation-delay: 0.1s; }
.sim-ptt__bar:nth-child(4) { background: #ce42c8; animation-delay: 0.15s; }
.sim-ptt__bar:nth-child(5) { background: #ab47bc; animation-delay: 0.2s; }
.sim-ptt__bar:nth-child(6) { background: #9c27b0; animation-delay: 0.25s; }
.sim-ptt__bar:nth-child(7) { background: #7e57c2; animation-delay: 0.3s; }
.sim-ptt__bar:nth-child(8) { background: #5c6bc0; animation-delay: 0.35s; }
.sim-ptt__bar:nth-child(9) { background: #42a5f5; animation-delay: 0.4s; }
.sim-ptt__bar:nth-child(10) { background: #29b6f6; animation-delay: 0.45s; }
.sim-ptt__bar:nth-child(11) { background: #26c6da; animation-delay: 0.5s; }
.sim-ptt__bar:nth-child(12) { background: #039be5; animation-delay: 0.55s; }

.sim-ptt.is-talking .sim-ptt__bar {
  animation-duration: 0.7s;
}

.sim-ptt__label {
  font-size: 11px;
  color: #b0b6c3;
}

.sim-btn {
  flex-shrink: 0;
  height: 36px;
  padding: 0 12px;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
}

.sim-btn--reset,
.sim-btn--close {
  border: 1px solid #d9dce3;
  background: #fff;
  color: #4a5160;
}

.sim-btn--stop {
  border: none;
  background: #f05252;
  color: #fff;
}

.sim-btn--start {
  border: none;
  background: #3d63ff;
  color: #fff;
}

@keyframes sim-ptt-sweep {
  0%,
  100% {
    height: 8px;
    opacity: 0.45;
  }
  50% {
    height: 24px;
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .sim-ptt__bar {
    animation: none;
  }
}
</style>
